import React, { useEffect, useState, useMemo, useCallback, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
// Phosphor icons, one file each (keeps the bundle to just these eleven).
// Every Phosphor icon ships a matching outline ("regular") and solid ("fill")
// drawing — the bar shows the outline at rest and the solid shape on the
// active tab, the way the Booking / Airbnb / iOS tab bars read. Lucide has
// no solid variants, so its active tab could only get a thicker stroke.
import { House } from '@phosphor-icons/react/dist/csr/House';
import { Bed } from '@phosphor-icons/react/dist/csr/Bed';
import { AirplaneTilt } from '@phosphor-icons/react/dist/csr/AirplaneTilt';
import { MapTrifold } from '@phosphor-icons/react/dist/csr/MapTrifold';
import { SuitcaseRolling } from '@phosphor-icons/react/dist/csr/SuitcaseRolling';
import { Heart } from '@phosphor-icons/react/dist/csr/Heart';
import { UserCircle } from '@phosphor-icons/react/dist/csr/UserCircle';
import { Mosque } from '@phosphor-icons/react/dist/csr/Mosque';
import { Tag } from '@phosphor-icons/react/dist/csr/Tag';
import { Buildings } from '@phosphor-icons/react/dist/csr/Buildings';
import { IdentificationCard } from '@phosphor-icons/react/dist/csr/IdentificationCard';
import { MagnifyingGlass } from '@phosphor-icons/react/dist/csr/MagnifyingGlass';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { readSiteFeatures, isFeatureEnabled } from '../../utils/siteFeatures';
import useProDesign from '../../hooks/useProDesign';
import './MobileBottomNav.css';

// Lazy-loaded so the OAuth chunk doesn't bloat the initial bundle. The sheet
// only mounts once the user actually taps "Sign in".
const SignInSheet = lazy(() => import('../Common/SignInSheet'));

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE BOTTOM NAVIGATION
   ─────────────────────────
   Persistent 5-slot tab bar that appears on phones / small tablets only.
   Mirrors the pattern used by every modern travel app (Booking, Airbnb,
   Hopper, Expedia, Trip.com, Skyscanner).

   Design goals:
     • Always shows EXACTLY one home + up to 4 contextual items.
     • Every slot is feature-toggle aware — if `flights` is disabled the
       Flights tab vanishes, and the next available feature takes its slot.
     • Wishlist + My Bookings always available (they don't depend on a paid
       feature; they're personal pages). Account = sign-in if signed-out.
     • Safe-area aware (iPhone notch / Android gesture bar).
     • Hides on admin / onboarding routes and when a modal locks body scroll.
     • Hides on ≥ lg (1024px) so the desktop Navbar is the single source of
       truth on big screens.
     • Exposes its own height as `--mbn-height` so other fixed UI (ChatBot
       FAB, WhatsApp FAB, sticky CTAs) can lift above it on phones.
   ═══════════════════════════════════════════════════════════════════════════ */

// Routes where the bottom nav must NEVER appear — they have their own chrome
// or are final-payment funnels that should stay focused (admin dashboard,
// onboarding wizard, and every checkout funnel: generic confirm-booking,
// WebBeds hotel booking, post-payment result).
const HIDDEN_PATH_PREFIXES = [
    '/private',
    '/onboarding',
    '/confirm-booking',
    '/webbeds-book',
    '/hotelbeds-book',
    '/payment/result',
    '/b2b/portal',
];

// Funnels whose path has a dynamic segment in the middle (so a simple prefix
// match won't catch them). The tour-booking step is `/tours/:code/book`.
const HIDDEN_PATH_PATTERNS = [
    /^\/tours\/[^/]+\/book\/?$/,
];

// "Search" tab on mobile goes to the most relevant search experience for
// the tenant. Hotels > Flights > Tours fallback chain — same priority used
// by the Hero search-widget tab selector.
// `label` holds a translation KEY (bottomNav.*), resolved with t() at render.
const resolveSearchTarget = (features) => {
    if (isFeatureEnabled(features, 'ratehawk') || isFeatureEnabled(features, 'webbeds')) {
        return { to: '/hotels', label: 'bottomNav.hotels', Icon: Bed };
    }
    if (isFeatureEnabled(features, 'flights')) {
        return { to: '/flights', label: 'bottomNav.flights', Icon: AirplaneTilt };
    }
    if (isFeatureEnabled(features, 'tours')) {
        return { to: '/tours', label: 'bottomNav.tours', Icon: MapTrifold };
    }
    return null;
};

// Build the available secondary slots in priority order. Only the first
// `availableCount` (up to 2) will actually render between Search and Wishlist.
const buildExtraSlots = (features, hasSearchTab) => {
    const slots = [];

    // Deals — high engagement, low friction
    if (isFeatureEnabled(features, 'deals')) {
        slots.push({ to: '/deals', label: 'bottomNav.deals', Icon: Tag });
    }
    // Hajj & Umrah — religious / regional traffic, deeply intentional
    if (isFeatureEnabled(features, 'hajjUmrah')) {
        slots.push({ to: '/hajj-umrah', label: 'bottomNav.umrah', Icon: Mosque });
    }
    // Partner hotels (offline). On a site with no other hotel search (e.g. a
    // Makkah & Madinah site) this list IS the hotels page, so it is labelled
    // "Hotels" with a bed — "Partner" meant nothing to a guest there.
    if (isFeatureEnabled(features, 'offlineHotels')) {
        slots.push(hasSearchTab
            ? { to: '/hotels-partner', label: 'bottomNav.partner', Icon: Buildings }
            : { to: '/hotels-partner', label: 'bottomNav.hotels', Icon: Bed });
    }
    // Visa
    if (isFeatureEnabled(features, 'visa')) {
        slots.push({ to: '/visa', label: 'bottomNav.visa', Icon: IdentificationCard });
    }
    return slots;
};

// Detect whether body scroll is locked (a modal/sheet is open). When it is,
// the bottom bar would float on top of the dialog backdrop and look broken,
// so we hide it. Matches the same pattern index.css uses to hide the FABs.
const useBodyScrollLocked = () => {
    const [locked, setLocked] = useState(false);
    useEffect(() => {
        const check = () => {
            const inline = document.body.style.overflow || '';
            setLocked(inline.includes('hidden'));
        };
        check();
        // body.style.overflow changes aren't observable directly — fall back to
        // MutationObserver on the style attribute (cheap on a single element).
        const obs = new MutationObserver(check);
        obs.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
        return () => obs.disconnect();
    }, []);
    return locked;
};

const MobileBottomNav = () => {
    const location = useLocation();
    // The "Airbnb Pro" design brings its own, quieter bar (see the pro branch
    // in the tab list below). Every other design keeps this bar exactly.
    const proNav = useProDesign();
    const { t } = useTranslation();
    const { userSession, user } = useAuth() || {};
    const signedIn = !!(userSession || user);

    const [features, setFeatures] = useState(readSiteFeatures);
    useEffect(() => {
        const refresh = () => setFeatures(readSiteFeatures());
        window.addEventListener('hm-settings-loaded', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener('hm-settings-loaded', refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);

    const bodyLocked = useBodyScrollLocked();
    const [signInOpen, setSignInOpen] = useState(false);

    // Build the visible tab list — ALWAYS up to 5 slots, layout is stable.
    // The 5th slot adapts to auth state, eliminating the previous redundancy
    // where both "Trips" and "Account" pointed to the same page.
    //
    // Guest (5 slots):  Home · Search · [extra] · Saved · Sign in
    // Signed in:        Home · Search · [extra] · Trips · Saved
    //
    // Why this order works:
    //   - Guests need a clear sign-in CTA AND a way to save favourites
    //     before they sign in (we persist wishlist to localStorage already).
    //   - Signed-in users get direct access to their bookings + saved items
    //     without an account-page detour. The Navbar profile dropdown still
    //     handles sign-out, settings, etc. — no functionality lost.
    const tabs = useMemo(() => {
        const list = [];

        // Airbnb Pro: four tabs, Airbnb's order. "Explore" is the home page —
        // its search covers hotels, flights and tours, so a separate Hotels
        // tab only repeated it. Guest:  Explore · Deals · Saved · Log in.
        // Signed in: Explore · Deals · Saved · Trips.
        if (proNav) {
            const openSignIn = (e) => { e.preventDefault(); setSignInOpen(true); };
            list.push({ key: 'home', to: '/', label: 'bottomNav.explore', Icon: MagnifyingGlass, match: (p) => p === '/' || p === '' });
            const extra = buildExtraSlots(features, !!resolveSearchTarget(features))[0];
            if (extra) list.push({ key: extra.to, to: extra.to, label: extra.label, Icon: extra.Icon, match: (p) => p.startsWith(extra.to) });
            list.push(signedIn
                ? { key: 'wishlist', to: '/wishlist', label: 'bottomNav.saved', Icon: Heart, match: (p) => p.startsWith('/wishlist') }
                : { key: 'wishlist', to: '#', label: 'bottomNav.saved', Icon: Heart, match: () => false, onClick: openSignIn });
            list.push(signedIn
                ? { key: 'trips', to: '/my-bookings', label: 'bottomNav.trips', Icon: SuitcaseRolling, match: (p) => p.startsWith('/my-bookings') }
                : { key: 'signin', to: '#', label: 'bottomNav.logIn', Icon: UserCircle, match: () => false, onClick: openSignIn });
            return list;
        }

        list.push({
            key: 'home',
            to: '/',
            label: 'bottomNav.home',
            Icon: House,
            match: (p) => p === '/' || p === '',
        });

        const searchTarget = resolveSearchTarget(features);
        if (searchTarget) {
            list.push({
                key: 'search',
                to: searchTarget.to,
                label: searchTarget.label,
                Icon: searchTarget.Icon,
                match: (p) => p.startsWith(searchTarget.to),
            });
        }

        // Pick up to N contextual extras.
        // - Signed-in users: Home + Search + Trips + Saved already = 4 fixed
        //   slots, leaving room for 1 extra if Search exists, else 2.
        // - Guests: Home + Search + Saved + Sign in = 4 fixed slots, same
        //   budget. Symmetric so the bar layout doesn't visually shift on
        //   login/logout.
        const extras = buildExtraSlots(features, !!searchTarget);
        const extrasBudget = searchTarget ? 1 : 2;
        extras.slice(0, extrasBudget).forEach((slot) => {
            list.push({
                key: slot.to,
                to: slot.to,
                label: slot.label,
                Icon: slot.Icon,
                match: (p) => p.startsWith(slot.to),
            });
        });

        // Slot 4: Trips (signed in) / Saved (guest)
        if (signedIn) {
            list.push({
                key: 'trips',
                to: '/my-bookings',
                label: 'bottomNav.trips',
                Icon: SuitcaseRolling,
                match: (p) => p.startsWith('/my-bookings'),
            });
            // Slot 5: Saved/Wishlist — signed-in users also want fast access
            // to their favourites, and we already have Trips covering bookings.
            list.push({
                key: 'wishlist',
                to: '/wishlist',
                label: 'bottomNav.saved',
                Icon: Heart,
                match: (p) => p.startsWith('/wishlist'),
            });
        } else {
            // Guests: Saved tab still rendered (UI affordance stays consistent
            // regardless of auth state), BUT tapping it opens the global
            // SignInSheet instead of navigating. This matches Booking/Airbnb:
            // the wishlist is a per-account feature, so the only thing a
            // guest tap can produce is the prompt to sign in.
            list.push({
                key: 'wishlist',
                to: '#',
                label: 'bottomNav.saved',
                Icon: Heart,
                match: () => false,
                onClick: (e) => {
                    e.preventDefault();
                    setSignInOpen(true);
                },
            });
            // Slot 5: Sign in — opens a dedicated bottom sheet with the
            // Google login button (no detour through the hamburger drawer).
            list.push({
                key: 'signin',
                to: '#',
                label: 'bottomNav.signIn',
                Icon: UserCircle,
                match: () => false,
                onClick: (e) => {
                    e.preventDefault();
                    setSignInOpen(true);
                },
            });
        }

        // Defensive trim to 5 — the algorithm already produces at most 5 in
        // every branch, but a future refactor could regress this and break
        // the grid layout, so we belt-and-brace.
        return list.slice(0, 5);
    }, [features, signedIn, proNav]);

    // Should we render at all on this route?
    const hiddenRoute =
        HIDDEN_PATH_PREFIXES.some((p) =>
            location.pathname === p || location.pathname.startsWith(`${p}/`)
        ) || HIDDEN_PATH_PATTERNS.some((re) => re.test(location.pathname));

    // Mark the document with a CSS hook so global CSS (FAB lift, page-bottom
    // padding, etc.) can react to the bar's presence without each component
    // needing to import the React state.
    useEffect(() => {
        const visible = !hiddenRoute && !bodyLocked && tabs.length > 1;
        const root = document.documentElement;
        if (visible) {
            root.setAttribute('data-mbn', 'on');
        } else {
            root.removeAttribute('data-mbn');
        }
        return () => root.removeAttribute('data-mbn');
    }, [hiddenRoute, bodyLocked, tabs.length]);

    const isActive = useCallback((tab) => tab.match(location.pathname), [location.pathname]);

    if (hiddenRoute) return null;
    if (tabs.length <= 1) return null; // nothing meaningful to show

    return (
        <>
            <nav
                className={`mbn-root ${proNav ? 'mbn-root--pro' : ''} ${bodyLocked ? 'mbn-root--hidden' : ''}`}
                aria-label="Primary mobile navigation"
                role="navigation"
            >
                <ul className="mbn-list" role="list">
                    {tabs.map((tab) => {
                        const active = isActive(tab);
                        const { Icon } = tab;
                        return (
                            <li key={tab.key} className="mbn-item">
                                <Link
                                    to={tab.to}
                                    onClick={tab.onClick || undefined}
                                    className={`mbn-link ${active ? 'mbn-link--active' : ''}`}
                                    aria-current={active ? 'page' : undefined}
                                    aria-label={t(tab.label)}
                                >
                                    <span className="mbn-icon-wrap" aria-hidden="true">
                                        <Icon size={proNav ? 25 : 24} weight={proNav ? (active ? 'bold' : 'regular') : (active ? 'fill' : 'regular')} />
                                    </span>
                                    <span className="mbn-label">{t(tab.label)}</span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {/* Sign-in bottom sheet — lazy-loaded; only mounts once opened.
                Auto-closes + navigates to /my-bookings on success, so the user
                lands on the page they almost always intended. */}
            <Suspense fallback={null}>
                {signInOpen && (
                    <SignInSheet open={signInOpen} onClose={() => setSignInOpen(false)} />
                )}
            </Suspense>
        </>
    );
};

export default MobileBottomNav;
